import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { MaskedLines } from "@/components/site/Reveal";
import { GuaranteeLine } from "@/components/site/Guarantee";
import { IMG } from "@/lib/content";
import { Price } from "@/components/site/Price";

const Portrait = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 120, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={reset} className="relative [perspective:1200px]" data-testid="hero-portrait">
      <motion.div style={{ rotateX: rx, rotateY: ry }} className="relative [transform-style:preserve-3d]">
        <motion.div initial={{ clipPath: "inset(100% 0% 0% 0%)" }} animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
          transition={{ duration: 1.4, ease: [0.76, 0, 0.24, 1], delay: 0.35 }}
          className="relative aspect-[4/5] overflow-hidden bg-[#0b0b0b]">
          <motion.img src={IMG.founder} alt="Dilara, founder of First Mover OS" style={{ y: imgY, scale: 1.15 }}
            fetchpriority="high" className="h-full w-full object-cover object-[50%_15%]" />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
        </motion.div>
        <div className="absolute -left-4 bottom-10 border border-line bg-[#050505]/90 px-5 py-4 backdrop-blur-md md:-left-12 [transform:translateZ(60px)]">
          <p className="font-display text-sm text-white">Dilara</p>
          <p className="text-xs text-zinc-400">Founder, First Mover OS</p>
        </div>
        <span className="absolute -right-3 -top-3 h-6 w-6 bg-cta [transform:translateZ(80px)]" aria-hidden="true" />
      </motion.div>
    </div>
  );
};

export const Hero = () => (
  <section data-testid="hero-section" className="relative overflow-hidden pt-28 md:pt-36">
    <div className="spotlight pointer-events-none absolute inset-0" />
    <div className="relative mx-auto grid max-w-7xl gap-14 px-6 pb-20 md:px-12 lg:grid-cols-12 lg:gap-10 lg:pb-32">
      <div className="lg:col-span-7 lg:pt-10">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }} className="eyebrow flex items-center gap-3">
          <motion.span initial={{ width: 0 }} animate={{ width: 40 }} transition={{ duration: 1, delay: 0.2 }} className="inline-block h-px bg-cta" />
          For beginners without a business
        </motion.p>
        <h1 className="font-display mt-8 text-[2.6rem] font-medium leading-[1.02] tracking-[-0.03em] text-white sm:text-6xl lg:text-[5.4rem]">
          <MaskedLines testId="hero-headline" lines={[
            "Find your first",
            "business idea",
            <span key="l3">before <span className="font-serif-i text-zinc-300">the crowd</span> does.</span>,
          ]} />
        </h1>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.75 }}>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-zinc-300 md:text-lg">
            A 6-chapter Blueprint and a 180-page Workbook that show you how to filter market noise, spot the signals that matter and turn one of them into a real first offer. No experience, no audience, no guru tricks.
          </p>
          <div className="mt-10 flex flex-col gap-5 sm:flex-row sm:items-center">
            <Link to="/products/bundle" data-testid="hero-cta-button" className="btn-cta text-base">
              Start with the Bundle, <Price role="bundle" /> <ArrowRight size={18} />
            </Link>
          </div>
          <div className="mt-6"><GuaranteeLine /></div>
        </motion.div>
      </div>
      <div className="lg:col-span-5"><Portrait /></div>
    </div>
  </section>
);
